import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Card, { CardContent } from "./TakashiCard";
import SparklineChart from "./SparklineChart";
import watchlistData from "./watchlists.json";
import stockLists from "../models/marketStockLists.json"; // ✅ Import the stock tickers for each category


import "./MarketsView.css";

import MarketStatus from "../components/MarketStatus";

import { fetchWatchlistStocks, fetchStockHistory, fetchStock } from "../models/marketsService";
import SectionHeader from "./SectionHeader";
import Overview from "./Overview";  // ✅ Import Overview
import LiveMarkets from "./LiveMarkets";  // ✅ Import LiveMarkets
import WatchlistSummary from "../components/WatchlistSummary";
import TickerNewsManager from "../components/TickerNewsManager";
import DynamicHotspot from "../components/DynamicHotspot";
import { fetchTickerNews } from "../models/marketsService";
import TickerNewsList from "../components/TickerNewsList";      // ✅ Import `TickerNews`

import image_marketAtAGlance from "../assets/marketAtAGlance.png";      
import image_dateSelector from "../assets/selector.png";      
import image_feedHeader from "../assets/Feed Header.png";      
import image0_overviewModules from "../assets/overview-modules.png";      
import image1_pinnedPost from "../assets/Live Coverage/image1-pinnedPost.png";     
import image2_articleNvidia from "../assets/Live Coverage/image2-articleNvidia.png";
import image3_articleNYT from "../assets/Live Coverage/image3-articleNYT.png";
import image4_immersive from "../assets/Live Coverage/image4-Immersive.png";
import image5_articleBudget from "../assets/Live Coverage/image5-articleBudget.png";
import image6_podcast from "../assets/Live Coverage/image6-Podcast.png";
import image7_warren from "../assets/Live Coverage/image7-warren.png";
import image8_nvda from "../assets/Live Coverage/image8-nvda.png";

import { Sparkle, CaretUp, CaretDown } from "@phosphor-icons/react";

import { getTopHeadlines, getEverything, getEverythingWithSource } from "../models/newsService";

import Typography from "../components/Typography";
import { useTheme } from "../context/ThemeContext";
import useIsMobile from "../components/useIsMobile";

import stockNews from "../models/stockNews.json";
import cryptoNews from "../models/cryptoNews.json";
import etfNews from "../models/etfNews.json";
import mutualFundNews from "../models/mutualFundNews.json";

const swipeConfidenceThreshold = 3000;
const swipePower = (offset, velocity) => Math.abs(offset.x) * velocity.x;

const MarketsView = () => {
  const { theme } = useTheme();

  const tabContainerRef = useRef(null); // ✅ Ref for the scrolling tab container

  const { watchlistId = "overview" } = useParams();
  const navigate = useNavigate();
  const [watchlists, setWatchlists] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [stockData, setStockData] = useState({});

  const [mostActiveStocks, setMostActiveStocks] = useState([]);
  const [trendingStocks, setTrendingStocks] = useState([]);
  const [trendingCurrency, setTrendingCurrency] = useState([]);
  const [trendingETFs, setTrendingETFs] = useState([]);
  const [trendingFunds, setTrendingFunds] = useState([]);
  const [trendingCrypto, setTrendingCrypto] = useState([]);
  const [trendingCommodities, setTrendingCommodities] = useState([]);
  const [dayTopMovers, setDayTopMovers] = useState([]);
  const [weekTopMovers, setWeekTopMovers] = useState([]);
  const [selectedTrending, setSelectedTrending] = useState(trendingStocks);
  const [selectedTrendingKey, setSelectedTrendingKey] = useState(Date.now());

  //NEWS
  const [newsArticles, setNewsArticles] = useState([]);
  const [selectedNewsFilter, setSelectedNewsFilter] = useState("STOCK"); // Default filter
  const [isNewsExpanded, setIsNewsExpanded] = useState(true); // ✅ To toggle collapse

const fetchFilteredNews = async (filterType) => {
  try {
      setSelectedNewsFilter(filterType); // ✅ Update active filter selection

      // let query = "";
      // if (filterType === "STOCK") {
      //     query = "stocks OR markets";
      // } else if (filterType === "CRYPTO") {
      //     query = "crypto OR bitcoin";
      // } else if (filterType === "MUTUAL FUND") {
      //     query = "mutual funds OR investing";
      // } else if (filterType === "ETFs") {
      //     query = "ETFs OR exchange-traded funds OR S&P500";
      // }

      // const fetchedArticles = await getEverythingWithSource(query, "wsj.com, marketwatch.com");

      let tickers = [];
      let fetchedArticles = [];
      let trendingData = [];

      if (filterType === "STOCK") {
        fetchedArticles = stockNews;
        trendingData = trendingStocks;
      } else if (filterType === "CRYPTO") {
        fetchedArticles = cryptoNews;
        trendingData = trendingCrypto;
      } else if (filterType === "MUTUAL FUND") {
        fetchedArticles = mutualFundNews;
        trendingData = trendingFunds;
      } else if (filterType === "ETFs") {
        fetchedArticles = etfNews;
        trendingData = trendingETFs;
      }

      // console.log(filterType);
      // console.log(fetchedArticles);
      setSelectedTrending([...trendingData]);
      setSelectedTrendingKey(Date.now()); // 🔥 Forces key change

      // ✅ Filter by Source ID (Only WSJ & Business Insider) and title length ≤ 120
      const filteredArticles = fetchedArticles.filter(article => 
        article.urlToImage !== null &&
        article.title.length <= 120
      );

          
      // console.log(filteredArticles);

      setNewsArticles(filteredArticles); // ✅ Limit to 3 articles
  } catch (error) {
      console.error("Error fetching filtered news:", error);
  }
};

useEffect(() => {
  console.log("Updated WatchlistSummary with:", selectedTrending);
}, [selectedTrending]); // ✅ Runs when `selectedTrending` changes


  useEffect(() => {
    // console.log("Fetching news for:", selectedNewsFilter);
    fetchFilteredNews(selectedNewsFilter);
  }, [selectedNewsFilter]); // ✅ Fetches new news when the filter changes

  
  const fetchStockListData = async () => {
    try {
      const fetchStockDataWithHistory = async (tickers) => {
        if (!tickers || tickers.length === 0) return [];
        const response = await fetchWatchlistStocks(tickers.join(","));
  
        const formattedStocks = await Promise.all(response.map(async (stock) => {
          const { normalizedHistory, normalizedFuture } = await fetchStockHistory(stock.ticker);
  
          const minPrice = stock.day?.l || stock.min?.l || 0;
          const maxPrice = stock.day?.h || stock.min?.h || 1;
  
          const actualHistory = normalizedHistory.map(normValue => 
            minPrice + (normValue * (maxPrice - minPrice))
          );
  
          const latestHistoryPrice = actualHistory.length > 0 ? actualHistory[actualHistory.length - 1] : null;
          const latestPrice = latestHistoryPrice || stock.lastTrade?.p || stock.min?.c || stock.day?.c || 0;
  
          let companyName = stock.name || stock.companyName || null;
          if (!companyName) {
            try {
              const stockInfo = await fetchStock(stock.ticker);
              companyName = stockInfo?.results?.name || stock.ticker;
              companyName = companyName.includes(".") 
                ? companyName.split(".")[0] 
                : companyName.length > 20 
                ? companyName.substring(0, 20) + "" 
                : companyName;
            } catch (error) {
              console.error(`Error fetching company name for ${stock.ticker}:`, error);
              companyName = stock.ticker;
            }
          }
  
          return {
            ticker: stock.ticker || "N/A",
            name: companyName || "Unknown Company",
            price: latestPrice || 0,
            change: stock.todaysChangePerc || 0,
            history: normalizedHistory,
            future: normalizedFuture,
          };
        }));
  
        return formattedStocks;
      };
  
      // ✅ Fetch data for Most Active Stocks
      if (stockLists.most_active.length > 0) {
        const mostActiveData = await fetchStockDataWithHistory(stockLists.most_active);
        setMostActiveStocks(mostActiveData);
      }
  
      // ✅ Fetch data for Day Top Movers
      if (stockLists.day_top_movers.length > 0) {
        const dayMoversData = await fetchStockDataWithHistory(stockLists.day_top_movers);
        setDayTopMovers(dayMoversData);
      }
  
      // ✅ Fetch data for Week Top Movers
      if (stockLists.week_top_movers.length > 0) {
        const weekMoversData = await fetchStockDataWithHistory(stockLists.week_top_movers);
        setWeekTopMovers(weekMoversData);
      }

      if (stockLists.trendingStocks.length > 0) {
        const trendingStocksData = await fetchStockDataWithHistory(stockLists.trendingStocks);
        setTrendingStocks(trendingStocksData);
      }
      if (stockLists.trendingCurrency.length > 0) {
        const trendingCurrencyData = await fetchStockDataWithHistory(stockLists.trendingCurrency);
        setTrendingCurrency(trendingCurrencyData);
      }
      if (stockLists.trendingETFs.length > 0) {
        const trendingETFsData = await fetchStockDataWithHistory(stockLists.trendingETFs);
        setTrendingETFs(trendingETFsData);
      }
      if (stockLists.trendingCrypto.length > 0) {
        const trendingCryptoData = await fetchStockDataWithHistory(stockLists.trendingCrypto);
        setTrendingCrypto(trendingCryptoData);
      }
      if (stockLists.trendingFunds.length > 0) {
        const trendingFundsData = await fetchStockDataWithHistory(stockLists.trendingFunds);
        setTrendingFunds(trendingFundsData);
      }
      if (stockLists.trendingCommodities.length > 0) {
        const trendingCommoditiesData = await fetchStockDataWithHistory(stockLists.trendingCommodities);
        setTrendingCommodities(trendingCommoditiesData);
      }
  
    } catch (error) {
      console.error("Error fetching stock list data:", error);
    }
  };
  
  
  // ✅ Call fetch function inside useEffect
  useEffect(() => {
    fetchStockListData();
  }, []);
  
  useEffect(() => {
    if (trendingStocks.length > 0) {
      setSelectedTrending(trendingStocks);
    }
  }, [trendingStocks]); // ✅ Runs when `trendingStocks` is updated
  

  useEffect(() => {
    // ✅ Load from localStorage & JSON
    const storedWatchlists = JSON.parse(localStorage.getItem("watchlists")) || [];
    
    // ✅ Ensure both sources are included (Remove duplicates by `id`)
    const mergedWatchlists = [...watchlistData, ...storedWatchlists].reduce((acc, watchlist) => {
        if (!acc.some(w => w.id === watchlist.id)) acc.push(watchlist);
        return acc;
    }, []);

    // ✅ Include Overview & Live tabs
    const allTabs = [{ id: "overview", name: "Live Overview" }, { id: "live", name: "Market Data" }, ...mergedWatchlists];

    // console.log("All Tabs:", allTabs); // Debugging

    setWatchlists(allTabs);

    const initialIndex = allTabs.findIndex(w => w.id === watchlistId);
    setCurrentIndex(initialIndex !== -1 ? initialIndex : 0);

    // ✅ If not overview/live, fetch stock data
    if (watchlistId !== "overview" && watchlistId !== "live") {
      const currentWatchlist = mergedWatchlists.find(w => w.id === watchlistId);
      fetchStockData(currentWatchlist?.stocks || []);    }
  }, [watchlistId]);
  

  const fetchStockData = async (tickers) => {
    try {
      if (!tickers || tickers.length === 0) return;
      const response = await fetchWatchlistStocks(tickers.join(","));
      const formattedStocks = await Promise.all(response.map(async (stock) => {
        const { normalizedHistory, normalizedFuture } = await fetchStockHistory(stock.ticker);

        const minPrice = stock.day?.l || stock.min?.l || 0;
        const maxPrice = stock.day?.h || stock.min?.h || 1;

        const actualHistory = normalizedHistory.map(normValue => 
          minPrice + (normValue * (maxPrice - minPrice))
        );

        const latestHistoryPrice = actualHistory.length > 0 ? actualHistory[actualHistory.length - 1] : null;
        const latestPrice = latestHistoryPrice || stock.lastTrade?.p || stock.min?.c || stock.day?.c || 0;

        let companyName = stock.name || stock.companyName || null;
        if (!companyName) {
          try {
            const stockInfo = await fetchStock(stock.ticker);
            companyName = stockInfo?.results?.name || stock.ticker;
            companyName = companyName.includes(".") 
              ? companyName.split(".")[0] 
              : companyName.length > 20 
              ? companyName.substring(0, 20) + "" 
              : companyName;
          } catch (error) {
            console.error(`Error fetching company name for ${stock.ticker}:`, error);
            companyName = stock.ticker;
          }
        }


        return {
          ticker: stock.ticker || "N/A",
          name: companyName || "Unknown Company",
          price: latestPrice || 0,
          change: stock.todaysChangePerc || 0,
          history: normalizedHistory,
          future: normalizedFuture,
        };
      }));

      const stockMap = formattedStocks.reduce((acc, stock) => {
        acc[stock.ticker] = stock;
        return acc;
      }, {});

      setStockData(stockMap);
    } catch (error) {
      console.error("Error fetching stock data:", error);
    }
  };

  useEffect(() => {
    if (watchlists && watchlists.stocks?.length > 0) {
      fetchStockData(watchlists.stocks);
    }
  }, [watchlists]); // ✅ Ensures stock data updates independently
  

  useEffect(() => {
    // console.log("Stock Data Fetch Triggered:", watchlists.stocks);
    if (watchlists && watchlists.stocks?.length > 0) {
      fetchStockData(watchlists.stocks);
    }
  }, [watchlists]); // ✅ Ensures stock data updates independently

  let startX = 0;
  let startY = 0;
  const [selectedDay, setSelectedDay] = useState("TH");

    const headlines = {
      "M": {
        timing: "Updated 2/24 at 4:22pm EST",
        date: "2/24",
        isNow: false,
        title: "Markets Open Strong After Weekend Rally",
        body: "Investors remain optimistic as tech stocks lead the gains.",
        image: "path_to_monday_image.png",
      },
      "T": {
        timing: "Updated 2/25 at 9:49pm EST",
        date: "2/25",
        isNow: false,
        title: "Tech Stocks Slump Amid Regulatory Scrutiny",
        body: "New antitrust concerns shake up the market landscape.",
        image: "path_to_tuesday_image.png",
      },
      "W": {
        timing: "Updated 2/26 at 7:29pm EST",
        date: "2/26",
        isNow: false,
        title: "Federal Reserve Announces Interest Rate Decision",
        body: "Traders react as Fed unveils its latest monetary policy.",
        image: "path_to_wednesday_image.png",
      },
      "TH": {
        timing: "Updated 45 min ago",
        date: "2/27",
        isNow: true,
        title: "Bonds Rally, Stocks Decline",
        body: "Oil prices jumped after OPEC delayed a planned supply increase, while traders remained concerned about the Middle East disruptions.",
        image: "path_to_thursday_image.png",
      },
      "F": {
        isNow: false,
        title: "Wall Street Ends Week with Mixed Results",
        body: "Markets close as investors assess economic outlook.",
        image: "path_to_friday_image.png",
      }
    };

  


  const isMobile = useIsMobile(); // ✅ Detect mobile device

  return (
    <div className="markets-container" style={{ display: "flex", overflow: "hidden", width: "100%", height: "100vh" }}>
      <AnimatePresence initial={false} custom={currentIndex} mode="popLayout">
        {watchlists.map((watchlist, index) => (
          <motion.div
            key={watchlist.id}
            initial={{ x: (index - currentIndex) * 100 + "%" }}
            animate={{ x: (index - currentIndex) * 100 + "%" }}
            exit={{ x: (index - currentIndex) * 100 + "%" }}
            transition={{ type: "tween", duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
            drag={isMobile?"":"x"}
            dragConstraints={{ left: 0, right:0 }} // Allow a bit more drag freedom
            dragElastic={0.2}  // Reduces swipe sensitivity
            dragMomentum={false} // ✅ Prevents it from continuing to move after the drag ends

          
            // ✅ Fix: Prevent conflicts with vertical scrolling
            onTouchMove={(e) => {
              if (Math.abs(e.changedTouches[0].clientX - e.touches[0].clientX) > 300) {
                e.preventDefault(); // Prevents accidental horizontal swipe while scrolling
              }
            }}
            onDragStart={(e, { point }) => {
              startX = point.x;
              startY = point.y;
            }}
          
            // 🔥 Check if the swipe is more horizontal than vertical before navigating
            onDragEnd={(e, { offset, velocity, point }) => {
              const deltaX = Math.abs(point.x - startX);
              const deltaY = Math.abs(point.y - startY);
          
              if (deltaX > deltaY) { // ✅ Only trigger swipe if it's more horizontal
                const swipe = swipePower(offset, velocity);
                if (swipe < -swipeConfidenceThreshold) {
                  navigate(`/markets/watchlist/${watchlists[(currentIndex + 1) % watchlists.length].id}`);
                } else if (swipe > swipeConfidenceThreshold) {
                  navigate(`/markets/watchlist/${watchlists[(currentIndex - 1 + watchlists.length) % watchlists.length].id}`);
                }
              }
            }}
            style={{ flexShrink: 0, width: "100%", position: "absolute", top: 0, left: 0 }}
          >
            <div className="content" id="scroll-container-watchlist">
              {watchlist.id === "overview" ? (
                <>
                <div className="filter-scroll-container week">
                  <div className="filter-button-container" style={{ display: "flex", gap: "8px", padding: "16px 14px 8px" }}>
                    <div className="week-selector">
                    {Object.keys(headlines).map((day) => (
                      <button 
                        key={day} 
                        onClick={() => setSelectedDay(day)}
                        className={selectedDay === day ? "selected-filter" : ""}
                        disabled={day === "F"}
                      >
                        {
                        selectedDay === day ?
                        day + " " + headlines[selectedDay].date : day}
                      </button>
                    ))}
                    </div>
                    <button className="view-all">View All</button>
                  </div>
                </div>
                <div className="feed-header">
                  <div className={headlines[selectedDay].isNow ? "timing live" : "timing concluded"}>
                  {headlines[selectedDay].isNow ? 
                  <div className="flashline live"><div className="live-icon"/><p>Live</p></div>
                  :
                  <div className="flashline concluded"><p>Concluded</p></div>
                  }
                  <p>{headlines[selectedDay].timing}</p>
                  </div>
                  <div className="feed-headline">{headlines[selectedDay].title}</div>
                  <div className="feed-body">{headlines[selectedDay].body}</div>
                </div>
                <Overview selectedDate={selectedDay}/>
                <img className="figma-image" src={image0_overviewModules}></img>
                <img className="figma-image" src={image_marketAtAGlance}></img>
                <div className="article-collection">
                  {/* <img className="figma-image pinnedPost" src={image1_pinnedPost}></img> */}
                  <img className="figma-image" src={image2_articleNvidia}></img>
                  <img className="figma-image" src={image3_articleNYT}></img>
                  <img className="figma-image" src={image4_immersive}></img>
                  <img className="figma-image" src={image5_articleBudget}></img>
                  <img className="figma-image" src={image6_podcast}></img>
                  <img className="figma-image" src={image7_warren}></img>
                  <img className="figma-image" src={image8_nvda}></img>
                </div>


          
                </>
                
              ) : watchlist.id === "live" ? (
                <>
                {/* Trending Stocks */}
                
                <div className="filter-scroll-container">
                  <div className="filter-button-container" style={{ display: "flex", gap: "px", padding: "16px 14px 16px" }}>
                      {["STOCK", "CRYPTO", "ETFs", "MUTUAL FUND"].map((filter) => (
                        <button 
                        key={filter} 
                        onClick={() => fetchFilteredNews(filter)}
                        className={selectedNewsFilter===filter?"selected-filter":""}
                        >
                              {filter}
                          </button>
                      ))}
                  </div>
                </div>
                      <MarketStatus/>
                <WatchlistSummary key={selectedTrendingKey} watchlist={selectedTrending} subjectArea={"trending"} isActive={true} />
              <SectionHeader title="Trending" />
              <div className="stock-list">
                {selectedTrending.map((stock) => (
                  stock.price &&
                  <Card key={stock.ticker} className="card" onClick={() => {navigate(`/company/${stock.ticker}`);}}>
                  <CardContent>
                    <div className="CompanyDetails">
                    <div className="ticker">
                            <div className={stock.change >= 0 ? "ticker-icon positive" : "ticker-icon negative"}>
                              {stock.change >= 0 ? "▲" : "▼"}
                            </div>
                            <h2 className="font-semibold">{stock.ticker}</h2>
                          </div>
                          <p className="text-sm text-gray-500">{stock.name || "Unknown Stock"}</p>
                    </div>
                    <SparklineChart data={stock.history || []} isPositive={stock.change >= 0} ticker={stock.ticker} />
                    <div className="Stock-Details">
                      <p className="text-lg font-bold">
                        ${stock.price ? stock.price.toFixed(2) : "N/A"}
                      </p>
                      <p className={stock.change < 0 ? "text-red-500 font-semibold" : "text-green-500 font-semibold"}>
                        {stock.change ? stock.change.toFixed(2) + "%" : "N/A"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                ))}
                <div className="view-all">View All Trending</div>
              </div>
                <div className="overview-divider"></div>
                 {/* News Filters */}
                {/* News Section with Filters */}
                <div 
                    style={{ padding: "16px", display: "flex", justifyContent: "space-between", cursor: "pointer" }}
                    onClick={() => setIsNewsExpanded(!isNewsExpanded)}
                >
                    <Typography variant="strap" type="utility">In the News</Typography>
                    <span>{isNewsExpanded ? <CaretUp/> : <CaretDown/>}</span>
                </div>
                {/* News List - Collapsible */}
                <AnimatePresence>
                    {isNewsExpanded && (
                        <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            style={{ overflow: "hidden" }}
                        >
                          <TickerNewsList key={index} tickerNews={newsArticles} isCompany={false}/> 
                        </motion.div>
                    )}
                </AnimatePresence>
                <div className="overview-divider"></div>
              <SectionHeader title="Most Active" />
              <div className="stock-list">
                {mostActiveStocks.map((stock) => (
                  <Card key={stock.ticker} className="card" onClick={() => {navigate(`/company/${stock.ticker}`);}}>
                  <CardContent>
                    <div className="CompanyDetails">
                    <div className="ticker">
                            <div className={stock.change >= 0 ? "ticker-icon positive" : "ticker-icon negative"}>
                              {stock.change >= 0 ? "▲" : "▼"}
                            </div>
                            <h2 className="font-semibold">{stock.ticker}</h2>
                          </div>
                          <p className="text-sm text-gray-500">{stock.name || "Unknown Stock"}</p>
                    </div>
                    <SparklineChart data={stock.history || []} isPositive={stock.change >= 0} ticker={stock.ticker} />
                    <div className="Stock-Details">
                      <p className="text-lg font-bold">
                        ${stock.price ? stock.price.toFixed(2) : "N/A"}
                      </p>
                      <p className={stock.change < 0 ? "text-red-500 font-semibold" : "text-green-500 font-semibold"}>
                        {stock.change ? stock.change.toFixed(2) + "%" : "N/A"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                ))}
                <div className="view-all">View All Most Active</div>
              </div>

              {/* Day Top Movers */}
              <div className="overview-divider"></div>

              <SectionHeader title="Day Top Movers" />
              <div className="stock-list">
                {dayTopMovers.map((stock) => (
                  <Card key={stock.ticker} className="card" onClick={() => {navigate(`/company/${stock.ticker}`);}}>
                  <CardContent>
                    <div className="CompanyDetails">
                    <div className="ticker">
                            <div className={stock.change >= 0 ? "ticker-icon positive" : "ticker-icon negative"}>
                              {stock.change >= 0 ? "▲" : "▼"}
                            </div>
                            <h2 className="font-semibold">{stock.ticker}</h2>
                          </div>
                          <p className="text-sm text-gray-500">{stock.name || "Unknown Stock"}</p>
                    </div>
                    <SparklineChart data={stock.history || []} isPositive={stock.change >= 0} ticker={stock.ticker} />
                    <div className="Stock-Details">
                      <p className="text-lg font-bold">
                        ${stock.price ? stock.price.toFixed(2) : "N/A"}
                      </p>
                      <p className={stock.change < 0 ? "text-red-500 font-semibold" : "text-green-500 font-semibold"}>
                        {stock.change ? stock.change.toFixed(2) + "%" : "N/A"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                ))}
                <div className="view-all">View All Day Top Movers</div>
              </div>

              {/* 52-Week Top Movers */}
              <div className="overview-divider"></div>

              <SectionHeader title="52-Week Top Movers" />
              <div className="stock-list">
                {weekTopMovers.map((stock) => (
                  <Card key={stock.ticker} className="card" onClick={() => {navigate(`/company/${stock.ticker}`);}}>
                  <CardContent>
                    <div className="CompanyDetails">
                    <div className="ticker">
                            <div className={stock.change >= 0 ? "ticker-icon positive" : "ticker-icon negative"}>
                              {stock.change >= 0 ? "▲" : "▼"}
                            </div>
                            <h2 className="font-semibold">{stock.ticker}</h2>
                          </div>
                          <p className="text-sm text-gray-500">{stock.name || "Unknown Stock"}</p>
                    </div>
                    <SparklineChart data={stock.history || []} isPositive={stock.change >= 0} ticker={stock.ticker} />
                    <div className="Stock-Details">
                      <p className="text-lg font-bold">
                        ${stock.price ? stock.price.toFixed(2) : "N/A"}
                      </p>
                      <p className={stock.change < 0 ? "text-red-500 font-semibold" : "text-green-500 font-semibold"}>
                        {stock.change ? stock.change.toFixed(2) + "%" : "N/A"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                ))}
                <div className="view-all">View All Week Top Movers</div>
              </div>

                </>
              ) : (
                <>
                 <div id="watchlist-summary">
                  <WatchlistSummary watchlist={watchlist} isActive={true} />
                  </div>
                  <SectionHeader title="Stocks" />

                  <div className="stock-list" id="stock-list">
                    {watchlist.stocks && watchlist.stocks.length > 0 ? (
                      watchlist.stocks.map((ticker) => {
                        const stock = stockData[ticker] || {};
                        return (
                          stock.price &&
                          <Card key={stock.ticker} className="card" onClick={() => {navigate(`/company/${stock.ticker}`);}}>
                            <CardContent>
                              <div className="CompanyDetails">
                              <div className="ticker">
                            <div className={stock.change >= 0 ? "ticker-icon positive" : "ticker-icon negative"}>
                              {stock.change >= 0 ? "▲" : "▼"}
                            </div>
                            <h2 className="font-semibold">{ticker}</h2>
                          </div>
                          <p className="text-sm text-gray-500">{stock.name || "Unknown Stock"}</p>
                              </div>
                              <SparklineChart data={stock.history || []} isPositive={stock.change >= 0} ticker={ticker} />
                              <div className="Stock-Details">
                                <p className="text-lg font-bold">
                                  ${stock.price ? stock.price.toFixed(2) : "N/A"}
                                </p>
                                <p className={stock.change < 0 ? "text-red-500 font-semibold" : "text-green-500 font-semibold"}>
                                  {stock.change ? stock.change.toFixed(2) + "%" : "N/A"}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })
                    ) : (
                      <p>No stocks in this watchlist.</p>
                    )}
                  </div>
                  {/*  ADD TickerNewsManager HERE, passing the watchlist  */}
                  <div style={{ padding: "24px 16px 16px 16px", backgroundColor: theme.colors.fillPrimary }}>
                    <Typography variant="strap" type="utility">In the News</Typography>
                  </div>
                  <TickerNewsManager watchlist={watchlist} />
                </>
              )}
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default MarketsView;
